import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Category } from "../types/gallery";
import {
  useAddCategoryMutation,
  useUpdateCategoryMutation,
} from "../api/galleriesApi";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  category?: Category | null;
}

const toSlug = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const AddEditCategory: React.FC<Props> = ({ open, setModalOpen, onSuccess, category }) => {
  const [formData, setFormData] = useState<{ name: string; slug: string }>({
    name: "",
    slug: "",
  });

  const [addCategory] = useAddCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        slug: category.slug || "",
      });
    } else {
      setFormData({
        name: "",
        slug: "",
      });
    }
  }, [category, open]);

  const handleSubmit = async () => {
    setIsSaving(true);
    setSubmissionError(null);
    try {
      if (!formData.name.trim()) {
        setSubmissionError("Name is required");
        setIsSaving(false);
        return;
      }
      const body = new FormData();
      body.append("name", formData.name);
      body.append("slug", formData.slug || toSlug(formData.name));

      if (category) {
        await updateCategory({ id: category.id.toString(), body }).unwrap();
      } else {
        await addCategory({ body }).unwrap();
      }

      setModalOpen(false);
      onSuccess?.();
    } catch (error: any) {
      setSubmissionError(
        error?.data?.message || `Failed to ${category ? "update" : "create"} category`
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{category ? "Edit Category" : "Add Category"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}

          <TextField
            label="Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <TextField
            label="Slug"
            fullWidth
            value={formData.slug}
            placeholder={toSlug(formData.name)}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : category ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditCategory;
